import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { UserPlus, Trash2, Pencil, Shield, Clock, Globe } from "lucide-react";
import { toast } from "sonner";

/**
 * Owner-only panel to invite administrators by email and manage their
 * "Can edit" / "Can moderate" permissions. Optionally exposes a Wikipedia-style
 * "allow public editing" toggle (owner keeps notifications + rollback).
 */
export default function AdministratorsManager({
    entityType,
    entityId,
    accent = "#ff4500",
    canManage = false,
    title = "Moderators",
    embedded = false,
    showPublicEdit = false,
    publicEdit = false,
    onTogglePublicEdit,
}) {
    const [rows, setRows] = useState(null);       // null = not owner / not loaded
    const [term, setTerm] = useState("");
    const [selected, setSelected] = useState(null); // chosen account {id, username, name, photo}
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [openList, setOpenList] = useState(false);
    const [canEdit, setCanEdit] = useState(true);
    const [canMod, setCanMod] = useState(true);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        if (!canManage) { setRows(null); return; }
        try {
            const { data } = await api.get(`/collaborators/${entityType}/${entityId}`);
            setRows(data);
        } catch {
            setRows(null); // not the owner — hide the panel
        }
    }, [entityType, entityId, canManage]);

    useEffect(() => { load(); }, [load]);

    // Type-ahead account lookup (same as the "New message" composer): when the text
    // isn't an email, search users by @username / name and offer them to pick.
    const looksLikeEmail = /\S+@\S+\.\S+/.test(term.trim());
    useEffect(() => {
        if (!term.trim() || looksLikeEmail || selected) { setResults([]); setSearching(false); return undefined; }
        let active = true; setSearching(true);
        const t = setTimeout(async () => {
            try {
                const { data } = await api.get("/messages/search-users", { params: { q: term.trim().replace(/^@/, "") } });
                if (active) { setResults(data.users || []); setOpenList(true); }
            } catch { if (active) setResults([]); }
            finally { if (active) setSearching(false); }
        }, 250);
        return () => { active = false; clearTimeout(t); };
    }, [term, looksLikeEmail, selected]);

    if (rows === null) return null;

    const pick = (u) => { setSelected(u); setTerm(`@${u.username}`); setResults([]); setOpenList(false); };

    const invite = async (e) => {
        e.preventDefault();
        const val = term.trim();
        if (!val && !selected) return;
        setBusy(true);
        try {
            const body = { can_edit: canEdit, can_moderate: canMod };
            if (selected) body.username = selected.username;
            else if (/\S+@\S+\.\S+/.test(val)) body.email = val;
            else body.username = val.replace(/^@/, "");
            const { data } = await api.post(`/collaborators/${entityType}/${entityId}`, body);
            setRows((r) => [...r, data]);
            setTerm(""); setSelected(null); setResults([]); setOpenList(false);
            toast.success(data.status === "pending" ? "Invite saved — activates when they sign up" : "Moderator added");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not add moderator");
        } finally { setBusy(false); }
    };

    const togglePerm = async (row, field) => {
        try {
            const { data } = await api.put(`/collaborators/${row.id}`, { [field]: !row[field] });
            setRows((r) => r.map((x) => (x.id === row.id ? data : x)));
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not update");
        }
    };

    const remove = async (id) => {
        try {
            await api.delete(`/collaborators/${id}`);
            setRows((r) => r.filter((x) => x.id !== id));
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not remove");
        }
    };

    const shell = embedded
        ? "border border-[#2a2a2a] bg-[#0f0f0f] p-4"
        : "mt-10 border border-[#2a2a2a] bg-[#141414] p-5";

    return (
        <section className={shell} data-testid="administrators-manager">
            {!embedded && (
                <h2 className="font-display text-2xl tracking-wide inline-flex items-center gap-2 mb-1">
                    <Shield className="h-5 w-5" style={{ color: accent }} /> {title}
                </h2>
            )}
            <p className="text-xs text-[#a0a0a0] mb-4">
                Add people by <span className="text-[#cfcfcf]">@username</span> or email to help edit this listing and/or moderate its board.
                Start typing a username to look them up. If they don't have an account yet, invite them by email and it activates when they sign up.
            </p>

            <form onSubmit={invite} className="flex flex-col sm:flex-row gap-2 mb-4" data-testid="administrator-invite-form">
                <div className="relative flex-1">
                    <Input
                        data-testid="administrator-email"
                        type="text"
                        value={term}
                        onChange={(e) => { setTerm(e.target.value); setSelected(null); }}
                        onFocus={() => { if (results.length) setOpenList(true); }}
                        placeholder="@username or moderator@email.com"
                        autoComplete="off"
                        className="h-10 rounded-none bg-[#0f0f0f] border-[#2a2a2a] w-full"
                    />
                    {openList && !selected && !looksLikeEmail && term.trim() && (
                        <div className="absolute z-20 left-0 right-0 mt-1 max-h-64 overflow-auto border border-[#2a2a2a] bg-[#0f0f0f] shadow-xl" data-testid="administrator-search-results">
                            {searching && results.length === 0 ? (
                                <div className="px-3 py-2 text-xs text-[#a0a0a0]">Searching…</div>
                            ) : results.length === 0 ? (
                                <div className="px-3 py-2 text-xs text-[#a0a0a0]">No accounts found — invite by email instead.</div>
                            ) : (
                                results.map((u) => (
                                    <button
                                        key={u.id}
                                        type="button"
                                        data-testid={`administrator-search-result-${u.username}`}
                                        onClick={() => pick(u)}
                                        className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-[#1a1a1a]"
                                    >
                                        {u.photo
                                            ? <img src={u.photo} alt={u.username} className="h-7 w-7 rounded-full object-cover border border-[#2a2a2a]" />
                                            : <div className="h-7 w-7 rounded-full bg-[#2a2a2a] text-white flex items-center justify-center text-xs font-bold uppercase">{(u.username || "?").charAt(0)}</div>}
                                        <div className="min-w-0">
                                            <div className="text-sm font-semibold text-white truncate">{u.username}</div>
                                            {u.name && u.name !== u.username && <div className="text-xs text-[#a0a0a0] truncate">{u.name}</div>}
                                        </div>
                                    </button>
                                ))
                            )}
                        </div>
                    )}
                </div>
                <label className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[#cfcfcf] px-2">
                    <input type="checkbox" data-testid="administrator-can-edit" checked={canEdit} onChange={(e) => setCanEdit(e.target.checked)} /> Edit
                </label>
                <label className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[#cfcfcf] px-2">
                    <input type="checkbox" data-testid="administrator-can-moderate" checked={canMod} onChange={(e) => setCanMod(e.target.checked)} /> Moderate
                </label>
                <Button type="submit" disabled={busy || (!term.trim() && !selected)} data-testid="administrator-invite-btn" style={{ backgroundColor: accent }} className="rounded-none uppercase tracking-[0.2em] text-xs font-semibold hover:opacity-90 h-10">
                    <UserPlus className="h-4 w-4 mr-1.5" /> Add
                </Button>
            </form>

            {rows.length === 0 ? (
                <div className="text-sm text-[#a0a0a0]" data-testid="administrators-empty">No moderators yet.</div>
            ) : (
                <div className="divide-y divide-[#2a2a2a] border border-[#2a2a2a]" data-testid="administrators-list">
                    {rows.map((c) => (
                        <div key={c.id} className="p-3 flex items-center justify-between gap-3" data-testid={`administrator-${c.id}`}>
                            <div className="min-w-0">
                                <div className="text-sm text-white truncate">{c.username ? `${c.username}` : (c.name || c.email)}</div>
                                <div className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0] inline-flex items-center gap-1.5">
                                    {c.name && c.username ? c.name : c.email}
                                    {c.status === "pending" && <span className="inline-flex items-center gap-1 text-[#ff4500]"><Clock className="h-3 w-3" /> pending</span>}
                                </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                                <button type="button" data-testid={`administrator-toggle-edit-${c.id}`} onClick={() => togglePerm(c, "can_edit")} className={`inline-flex items-center gap-1 px-2 py-1 border text-[10px] uppercase tracking-[0.15em] transition-colors ${c.can_edit ? "border-[#22c55e] text-[#22c55e] bg-[#22c55e]/10" : "border-[#2a2a2a] text-[#555]"}`}>
                                    <Pencil className="h-3 w-3" /> Edit
                                </button>
                                <button type="button" data-testid={`administrator-toggle-moderate-${c.id}`} onClick={() => togglePerm(c, "can_moderate")} className={`inline-flex items-center gap-1 px-2 py-1 border text-[10px] uppercase tracking-[0.15em] transition-colors ${c.can_moderate ? "border-[#22c55e] text-[#22c55e] bg-[#22c55e]/10" : "border-[#2a2a2a] text-[#555]"}`}>
                                    <Shield className="h-3 w-3" /> Mod
                                </button>
                                <button type="button" data-testid={`administrator-remove-${c.id}`} onClick={() => remove(c.id)} className="text-[#a0a0a0] hover:text-red-500" aria-label="Remove moderator">
                                    <Trash2 className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {showPublicEdit && (
                <label className="mt-4 flex items-start gap-2.5 cursor-pointer" data-testid="public-edit-toggle-label">
                    <input
                        type="checkbox"
                        data-testid="public-edit-toggle"
                        checked={!!publicEdit}
                        onChange={(e) => onTogglePublicEdit && onTogglePublicEdit(e.target.checked)}
                        className="mt-0.5"
                    />
                    <span className="text-xs text-[#cfcfcf] leading-relaxed">
                        <span className="inline-flex items-center gap-1.5 text-white font-medium">
                            <Globe className="h-3.5 w-3.5" style={{ color: accent }} /> Allow public editing (like Wikipedia)
                        </span>
                        <br />
                        Anyone signed in can improve this listing. You'll be notified of every change and can roll back anything you don't like from the listing's edit history.
                    </span>
                </label>
            )}
        </section>
    );
}
